import { withRetry } from '@core/domain/utils/asyncResilience.ts';
import type { EntregaFormState } from '../../domain/EntregaFormState.ts';
import {
  ENTREGA_MAP_TILE_ZOOM,
  OSM_TILE_SIZE,
  entregaMapTileCacheKey,
  osmTileUrl,
  tileKey,
  tilesForEntregaMapState,
} from '../../domain/entregaMapTileMath.ts';
import {
  entregaMapMarkersFromForm,
  entregaMapMarkersInView,
  type OfflineMapMarker,
} from '../../domain/entregaMapModel.ts';

const CACHE_NAME = 'entrega-map-tiles-v1';

const compositeUrlByKey = new Map<string, string>();

/** Cache API needs http(s) URLs; logical keys are encoded in the path. */
function cacheStorageUrl(storageKey: string): string {
  const origin = globalThis.location?.origin ?? 'http://localhost';
  return `${origin}/__entrega-map-cache/${encodeURIComponent(storageKey)}`;
}

function canUseDomCanvas(): boolean {
  return typeof document !== 'undefined' && typeof createImageBitmap === 'function';
}

/** Same viewport fallback as `tilesForEntregaMapState` (GPS first, else punto). */
function compositeViewportMarkers(state: EntregaFormState): OfflineMapMarker[] {
  const { markers } = entregaMapMarkersInView(state);
  if (markers.length > 0) return markers;
  if (state.puntoLat != null && state.puntoLng != null) {
    return [{ lat: state.puntoLat, lng: state.puntoLng, label: 'Punto', color: '#6750A4' }];
  }
  return [];
}

async function fetchTileBlob(url: string): Promise<Blob | null> {
  try {
    const res = await fetch(url, { mode: 'cors', cache: 'force-cache' });
    if (!res.ok) return null;
    return res.blob();
  } catch {
    return null;
  }
}

async function cachePut(storageKey: string, blob: Blob, required = false): Promise<void> {
  if (typeof caches === 'undefined') {
    if (required) throw new Error('Cache Storage no disponible para mapas offline.');
    return;
  }
  try {
    const cache = await caches.open(CACHE_NAME);
    await cache.put(cacheStorageUrl(storageKey), new Response(blob));
  } catch (e) {
    if (required) throw e instanceof Error ? e : new Error(String(e));
  }
}

async function cacheGet(storageKey: string): Promise<Blob | null> {
  if (typeof caches === 'undefined') return null;
  try {
    const cache = await caches.open(CACHE_NAME);
    const res = await cache.match(cacheStorageUrl(storageKey));
    return res?.ok ? res.blob() : null;
  } catch {
    return null;
  }
}

async function fetchTileBlobRequired(url: string): Promise<Blob> {
  return withRetry(
    async () => {
      const blob = await fetchTileBlob(url);
      if (!blob) throw new Error(`No se pudo descargar el tile OSM (${url}).`);
      return blob;
    },
    { maxAttempts: 3, baseDelayMs: 400, maxDelayMs: 2000, jitterRatio: 0.2 },
  );
}

async function resolveTileBlob(cacheKey: string, url: string, required: boolean): Promise<Blob | null> {
  let blob = await cacheGet(cacheKey);
  if (blob) return blob;
  if (!(globalThis.navigator?.onLine ?? true)) return null;
  if (required) {
    blob = await fetchTileBlobRequired(url);
    await cachePut(cacheKey, blob, true);
    return blob;
  }
  blob = await fetchTileBlob(url);
  if (blob) await cachePut(cacheKey, blob);
  return blob;
}

function drawMapMarkers(
  ctx: CanvasRenderingContext2D,
  markers: OfflineMapMarker[],
  width: number,
  height: number,
  westPx: number,
  northPx: number,
  worldW: number,
  worldH: number,
  lngToPx: (lng: number) => number,
  latToPx: (lat: number) => number,
): void {
  for (const m of markers) {
    const mx = ((lngToPx(m.lng) - westPx) / worldW) * width;
    const my = ((latToPx(m.lat) - northPx) / worldH) * height;
    if (!Number.isFinite(mx) || !Number.isFinite(my)) continue;
    ctx.beginPath();
    ctx.fillStyle = m.color || '#6750A4';
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3;
    ctx.arc(mx, my, Math.max(10, width * 0.018), 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
}

async function buildCompositeBlob(
  state: EntregaFormState,
  width: number,
  height: number,
  zoom: number = ENTREGA_MAP_TILE_ZOOM,
  maxTiles = 9,
  quality = 0.85,
  requireTiles = false,
): Promise<Blob | null> {
  if (!canUseDomCanvas()) return null;
  const tiles = tilesForEntregaMapState(state, zoom, maxTiles);
  if (tiles.length === 0) return null;
  const viewport = compositeViewportMarkers(state);
  if (viewport.length === 0) return null;
  const lats = viewport.map((m) => m.lat);
  const lngs = viewport.map((m) => m.lng);
  let minLat = Math.min(...lats);
  let maxLat = Math.max(...lats);
  let minLng = Math.min(...lngs);
  let maxLng = Math.max(...lngs);
  const padLat = Math.max((maxLat - minLat) * 0.25, 0.012);
  const padLng = Math.max((maxLng - minLng) * 0.25, 0.012);
  minLat -= padLat;
  maxLat += padLat;
  minLng -= padLng;
  maxLng += padLng;

  const n = 2 ** zoom;
  const lngToPx = (lng: number) => ((lng + 180) / 360) * n * OSM_TILE_SIZE;
  const latToPx = (lat: number) => {
    const latRad = (lat * Math.PI) / 180;
    return ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n * OSM_TILE_SIZE;
  };
  const westPx = lngToPx(minLng);
  const northPx = latToPx(maxLat);
  const eastPx = lngToPx(maxLng);
  const southPx = latToPx(minLat);
  const worldW = eastPx - westPx;
  const worldH = southPx - northPx;
  if (!(worldW > 0) || !(worldH > 0)) return null;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = '#F3EDF7';
  ctx.fillRect(0, 0, width, height);

  let drawn = 0;
  for (const tile of tiles) {
    const cacheKey = `tile/${tileKey(tile)}`;
    const blob = await resolveTileBlob(cacheKey, osmTileUrl(tile), requireTiles);
    if (!blob) {
      if (requireTiles) return null;
      continue;
    }
    const bitmap = await createImageBitmap(blob);
    const tileWestPx = (tile.x / n) * n * OSM_TILE_SIZE;
    const tileNorthPx = (tile.y / n) * n * OSM_TILE_SIZE;
    const dx = ((tileWestPx - westPx) / worldW) * width;
    const dy = ((tileNorthPx - northPx) / worldH) * height;
    const dw = (OSM_TILE_SIZE / worldW) * width;
    const dh = (OSM_TILE_SIZE / worldH) * height;
    ctx.drawImage(bitmap, dx, dy, dw, dh);
    bitmap.close();
    drawn += 1;
  }
  if (drawn === 0) return null;

  const pinMarkers = entregaMapMarkersFromForm(state);
  drawMapMarkers(
    ctx,
    pinMarkers.length > 0 ? pinMarkers : viewport,
    width,
    height,
    westPx,
    northPx,
    worldW,
    worldH,
    lngToPx,
    latToPx,
  );

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', quality));
}

export async function prefetchEntregaMapOsmTiles(state: EntregaFormState, maxTiles = 9): Promise<void> {
  if (!(globalThis.navigator?.onLine ?? true)) return;
  const tiles = tilesForEntregaMapState(state, ENTREGA_MAP_TILE_ZOOM, maxTiles);
  await Promise.all(
    tiles.map(async (tile) => {
      const cacheKey = `tile/${tileKey(tile)}`;
      if (await cacheGet(cacheKey)) return;
      const blob = await fetchTileBlobRequired(osmTileUrl(tile));
      await cachePut(cacheKey, blob, true);
    }),
  );
}

export function getEntregaMapCompositeUrl(state: EntregaFormState): string | null {
  const key = entregaMapTileCacheKey(state);
  if (!key) return null;
  return compositeUrlByKey.get(key) ?? null;
}

export async function warmEntregaMapComposite(
  state: EntregaFormState,
  width: number,
  height: number,
): Promise<void> {
  const key = entregaMapTileCacheKey(state);
  if (!key) return;
  if (compositeUrlByKey.has(key)) return;
  const compositeKey = `composite/${key}`;
  const cached = await cacheGet(compositeKey);
  if (cached) {
    compositeUrlByKey.set(key, URL.createObjectURL(cached));
    return;
  }
  const blob = await buildCompositeBlob(state, width, height);
  if (!blob) return;
  await cachePut(compositeKey, blob);
  compositeUrlByKey.set(key, URL.createObjectURL(blob));
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el mapa.'));
    reader.readAsDataURL(blob);
  });
}

/** High-resolution static map JPEG for PDF export. Its own cache entry (keyed by size) so it never
 *  reuses the smaller on-screen composite; shares the per-tile cache, so it also works offline. */
export async function getEntregaMapCompositeDataUrl(
  state: EntregaFormState,
  width: number,
  height: number,
): Promise<string | null> {
  const key = entregaMapTileCacheKey(state);
  if (!key) return null;
  const compositeKey = `composite/${key}@${width}x${height}`;
  let blob = await cacheGet(compositeKey);
  if (!blob) {
    // Prefetch tiles when online so PDF map is complete (not purple gaps).
    try {
      await prefetchEntregaMapOsmTiles(state);
    } catch {
      /* offline / flaky net → use whatever tiles already cached */
    }
    blob = await buildCompositeBlob(state, width, height, ENTREGA_MAP_TILE_ZOOM, 9, 0.92, true);
    // ponytail: requireTiles may fail offline with partial cache — retry best-effort.
    if (!blob) blob = await buildCompositeBlob(state, width, height, ENTREGA_MAP_TILE_ZOOM, 9, 0.92, false);
  }
  if (!blob) return null;
  if (!(await cacheGet(compositeKey))) await cachePut(compositeKey, blob);
  return blobToDataUrl(blob);
}

export async function hydrateEntregaMapCompositeFromCache(state: EntregaFormState): Promise<void> {
  const key = entregaMapTileCacheKey(state);
  if (!key || compositeUrlByKey.has(key)) return;
  const cached = await cacheGet(`composite/${key}`);
  if (cached) compositeUrlByKey.set(key, URL.createObjectURL(cached));
}
